import React, { useEffect, useState, useMemo } from 'react';
import './FeatureStyles.css';
import './KnowledgeBaseFeature.css';

const KnowledgeBaseAnimation = () => {
  const [entries, setEntries] = useState([]);
  const [phase, setPhase] = useState('learning');
  const [nodeCount, setNodeCount] = useState(0);

  const iconSvgs = {
    workflow: (
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <polyline points="23 4 23 10 17 10" />
        <polyline points="1 20 1 14 7 14" />
        <path d="M3.51 9a9 9 0 0114.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0020.49 15" />
      </svg>
    ),
    process: (
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2" />
        <rect x="9" y="3" width="6" height="4" rx="1" />
        <line x1="9" y1="12" x2="15" y2="12" />
        <line x1="9" y1="16" x2="15" y2="16" />
      </svg>
    )
  };

  const knowledgeEntries = useMemo(() => [
    { icon: 'workflow', title: 'Checkout Flow', type: 'workflow', confidence: 98 },
    { icon: 'process', title: 'User Registration', type: 'process', confidence: 95 },
    { icon: 'workflow', title: 'Payment Process', type: 'workflow', confidence: 97 },
    { icon: 'process', title: 'Password Reset', type: 'process', confidence: 94 },
  ], []);

  useEffect(() => {
    let timeout;
    let entryIndex = 0;

    const addEntry = () => {
      if (entryIndex < knowledgeEntries.length) {
        setEntries(prev => [...prev, knowledgeEntries[entryIndex]]);
        setNodeCount(prev => prev + 1);
        entryIndex++;
        timeout = setTimeout(addEntry, 1000);
      } else {
        setPhase('complete');
        timeout = setTimeout(() => {
          setEntries([]);
          setNodeCount(0);
          setPhase('learning');
          entryIndex = 0;
          addEntry();
        }, 3000);
      }
    };

    timeout = setTimeout(addEntry, 800);
    return () => clearTimeout(timeout);
  }, [knowledgeEntries]);

  return (
    <div className="feature-animation knowledge-base">
      <div className="knowledge-container">
        <div className="knowledge-left">
          <div className="knowledge-feed">
            {entries.filter(entry => entry).map((entry, idx) => (
              <div key={idx} className="knowledge-entry" style={{ animationDelay: `${idx * 0.1}s` }}>
                <div className="entry-icon">{iconSvgs[entry.icon]}</div>
                <div className="entry-content">
                  <div className="entry-header">
                    <span className="entry-title">{entry.title}</span>
                    <span className={`entry-type ${entry.type}`}>{entry.type}</span>
                  </div>
                  <div className="confidence-bar">
                    <div className="confidence-fill" style={{ width: `${entry.confidence}%` }}></div>
                    <span className="confidence-value">{entry.confidence}%</span>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {phase === 'learning' && entries.length > 0 && (
            <div className="learning-indicator">
              <div className="learning-dots"><span></span><span></span><span></span></div>
              <span>AI is learning...</span>
            </div>
          )}

          {phase === 'complete' && (
            <div className="complete-indicator">
              <span className="complete-icon">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                  <polyline points="20 6 9 17 4 12" />
                </svg>
              </span>
              <span>Knowledge base ready</span>
            </div>
          )}
        </div>

        <div className="neural-visual">
          <div className="neural-nodes">
            {Array.from({ length: 6 }).map((_, i) => (
              <div
                key={i}
                className={`neural-node ${i < nodeCount ? 'active' : ''}`}
                style={{
                  left: `${20 + (i % 3) * 30}%`,
                  top: `${25 + Math.floor(i / 3) * 40}%`
                }}
              >
                <div className="node-pulse"></div>
              </div>
            ))}
            <svg className="neural-connections" viewBox="0 0 100 100">
              <line x1="20" y1="25" x2="50" y2="25" className={nodeCount >= 2 ? 'active' : ''} />
              <line x1="50" y1="25" x2="80" y2="25" className={nodeCount >= 3 ? 'active' : ''} />
              <line x1="20" y1="25" x2="35" y2="65" className={nodeCount >= 4 ? 'active' : ''} />
              <line x1="50" y1="25" x2="65" y2="65" className={nodeCount >= 4 ? 'active' : ''} />
              <line x1="35" y1="65" x2="65" y2="65" className={nodeCount >= 5 ? 'active' : ''} />
            </svg>
          </div>
          <div className="neural-label">Application Knowledge Graph</div>
        </div>
      </div>
    </div>
  );
};

const KnowledgeBaseFeature = {
  icon: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
      <path d="M12 2a10 10 0 00-6.88 17.23l2.3-2.29A6 6 0 1118 12h2A10 10 0 0012 2z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  ),
  title: "Knowledge Base",
  description: "AI learns your application as it tests. Every interaction builds understanding of UI elements, user workflows, API patterns, and application behavior. This growing knowledge map enables smarter test creation, better healing suggestions, and comprehensive coverage recommendations.",
  animation: <KnowledgeBaseAnimation />
};

export default KnowledgeBaseFeature;
